import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import StripeCheckout from 'react-stripe-checkout';

import { PPD_CART_ITEM_TYPES } from '../../constants/ppd-purchase';
import Button from '../global/Button';
import PurchasePpdDownloadCartItemContainer from '../../containers/purchase/PurchasePpdDownloadCartItemContainer';
import { formatPriceForDisplay } from '../../util/format-money';
import { getAsset } from '../../util/assets';
import getConfig from '../../util/config';
import PurchasePpdPrintPackageCartItemContainer from '../../containers/purchase/PurchasePpdPrintPackageCartItemContainer';
import PurchasePpdAddressFormContainer from '../../containers/purchase/PurchasePpdAddressFormContainer';
import PurchasePpdIndividualPrintCartItemContainer from '../../containers/purchase/PurchasePpdIndividualPrintCartItemContainer';
import Alert from '../global/Alert';
import PurchaseAlbumHeaderContainer from '../../containers/purchase/PurchaseAlbumHeaderContainer';
import ContentWrap from '../global/ContentWrap';
import CheckoutPrice from './CheckoutPrice';
import PurchasePpdSubscriptionCartItemContainer from '../../containers/purchase/PurchasePpdSubscriptionCartItemContainer';
import PurchasePpdAlbumCustomPrintCartItemContainer from '../../containers/purchase/PurchasePpdAlbumCustomPrintCartItemContainer';
import AlbumCustomPrintShippingSelection from '../discover/AlbumCustomPrintShippingSelection';
import { canSelectAlbumCustomPrintShipping } from '../../util/custom-prints';
import { shouldDisplayBrandAttribution } from '../../util/ppd-purchase';
import PhotographerAttributionBanner from '../discover/PhotographerAttributionBanner';

class PurchasePpdCheckout extends Component {
  static propTypes = {
    cart: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.oneOf(Object.keys(PPD_CART_ITEM_TYPES)),
        photo: PropTypes.shape({
          id: PropTypes.string,
        }),
        printSize: PropTypes.shape({
          id: PropTypes.string,
        }),
        printPackage: PropTypes.shape({
          id: PropTypes.string,
        }),
        selectedIdentity: PropTypes.shape({
          id: PropTypes.string.isRequired,
          identity: PropTypes.shape({
            id: PropTypes.string.isRequired,
          }),
        }),
        albumCustomPrint: PropTypes.shape({
          id: PropTypes.string.isRequired,
        }),
        quantity: PropTypes.number,
      }),
    ),
    price: PropTypes.shape({
      total_price: PropTypes.shape({
        value: PropTypes.number,
        currency: PropTypes.string,
      }),
      total_shipping_cost: PropTypes.shape({
        value: PropTypes.number,
        currency: PropTypes.string,
      }),
      total_tax: PropTypes.shape({
        value: PropTypes.number,
        currency: PropTypes.string,
      }),
    }),
    loadingPrice: PropTypes.bool,
    checkout: PropTypes.func.isRequired,
    selectAlbumCustomPrint: PropTypes.func.isRequired,
    userId: PropTypes.string.isRequired,
    checkoutMutation: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    success: PropTypes.bool,
    userAddress: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      address_line_1: PropTypes.string,
      address_line_2: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      post_code: PropTypes.string,
      country: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      }),
    }),
    album: PropTypes.shape({
      id: PropTypes.string,
      photo_pricing: PropTypes.shape({
        id: PropTypes.string.isRequired,
      }),
    }),
    history: PropTypes.shape({
      push: PropTypes.func,
      replace: PropTypes.func,
    }),
    error: PropTypes.string,
  };

  static defaultProps = {
    cart: [],
    userId: '',
    albumId: '',
    address: {},
  };

  state = {
    showAddressMissingAlert: false,
  };

  componentDidUpdate(prevProps) {
    const { success, history, album, cart } = this.props;

    if (success) {
      history.push(`/purchase/album/${album.id}/success`);
      return;
    }

    if (prevProps.cart.length > 0 && cart.length === 0) {
      history.replace(`/purchase/album/${album.id}`);
    }
  }

  onReceivedStripeToken = stripeToken => {
    if (!stripeToken) {
      return;
    }

    const { userId, album, checkoutMutation, checkout, userAddress } =
      this.props;

    checkout({
      userId,
      album,
      addressId: get(userAddress, 'id'),
      email: stripeToken.email,
      stripeTokenId: stripeToken.id,
      stripePaymentMethodId: stripeToken.stripe_payment_method_id,
      stripeConnectAccountId: stripeToken.stripe_connect_account_id,
      checkoutMutation,
    });
  };

  onAlbumCustomPrintShippingSelect = includeShipping => {
    const { cart, selectAlbumCustomPrint } = this.props;

    cart.forEach(cartItem => {
      if (cartItem.type === PPD_CART_ITEM_TYPES.ALBUM_CUSTOM_PRINT) {
        selectAlbumCustomPrint(
          cartItem.selectedIdentity,
          cartItem.albumCustomPrint,
          includeShipping,
        );
      }
    });
  };

  cartHasShippableItems = () => {
    const { cart } = this.props;

    const hasShippableAlbumCustomPrints = cart
      .filter(
        cartItem => cartItem.type === PPD_CART_ITEM_TYPES.ALBUM_CUSTOM_PRINT,
      )
      .some(
        cartItem =>
          get(cartItem, 'albumCustomPrint.shipping_required') ||
          (get(cartItem, 'albumCustomPrint.shipping_enabled') &&
            get(cartItem, 'includeShipping')),
      );
    const hasPrints = !!cart.find(
      cartItem =>
        cartItem.type === PPD_CART_ITEM_TYPES.INDIVIDUAL_PRINT ||
        cartItem.type === PPD_CART_ITEM_TYPES.PRINT_PACKAGE,
    );

    return hasPrints || hasShippableAlbumCustomPrints;
  };

  getShippingSelectionAlbumCustomPrints = () => {
    const { cart } = this.props;

    return cart
      .filter(
        cartItem => cartItem.type === PPD_CART_ITEM_TYPES.ALBUM_CUSTOM_PRINT,
      )
      .map(cartItem => ({
        ...cartItem.albumCustomPrint,
        includeShipping: cartItem.includeShipping,
      }))
      .filter(canSelectAlbumCustomPrintShipping);
  };

  renderCartItem = cartItem => {
    const { album, cart } = this.props;

    switch (cartItem.type) {
      case PPD_CART_ITEM_TYPES.INDIVIDUAL_PRINT:
        return (
          <PurchasePpdIndividualPrintCartItemContainer
            key={`${cartItem.photo.id}-${cartItem.printSize.id}`}
            cartItem={cartItem}
          />
        );
      case PPD_CART_ITEM_TYPES.PRINT_PACKAGE:
        return (
          <PurchasePpdPrintPackageCartItemContainer
            key={`${cartItem.photo.id}-${cartItem.printPackage.id}`}
            cartItem={cartItem}
          />
        );
      case PPD_CART_ITEM_TYPES.DOWNLOAD:
        return (
          <PurchasePpdDownloadCartItemContainer
            key={cartItem.photo.id}
            cartItem={cartItem}
            album={album}
          />
        );
      case PPD_CART_ITEM_TYPES.SUBSCRIPTION:
        return (
          <PurchasePpdSubscriptionCartItemContainer
            key={cartItem.selectedIdentity.id}
            cartItem={cartItem}
            album={album}
          />
        );
      case PPD_CART_ITEM_TYPES.ALBUM_CUSTOM_PRINT: {
        // TODO: This should also check if the identity has already
        // purchased a subscription as custom prints are discounted
        // based on existing subscriptions as well.
        const onDiscount = cart
          .filter(item => item.type === PPD_CART_ITEM_TYPES.SUBSCRIPTION)
          .some(
            ({ selectedIdentity }) =>
              selectedIdentity.id === cartItem.selectedIdentity.id,
          );

        return (
          <PurchasePpdAlbumCustomPrintCartItemContainer
            key={`${cartItem.selectedIdentity.id}-${cartItem.albumCustomPrint.id}`}
            cartItem={cartItem}
            onDiscount={onDiscount}
          />
        );
      }
      default:
        return null;
    }
  };

  render() {
    const {
      cart,
      price,
      loadingPrice,
      loading,
      userAddress,
      userId,
      album,
      history,
      error,
    } = this.props;

    const { showAddressMissingAlert } = this.state;
    const config = getConfig();
    const totalPrice = get(price, 'total_price.value', 0);
    const shippingPrice = get(price, 'total_shipping_cost.value', 0);
    const taxes = get(price, 'total_tax.value', 0);
    const shippingSelectableAlbumCustomPrints =
      this.getShippingSelectionAlbumCustomPrints();
    const hasShippableItems = this.cartHasShippableItems();
    const canPurchase =
      totalPrice > 0 &&
      (!hasShippableItems || (hasShippableItems && !!get(userAddress, 'id')));

    return (
      <div className="wrapper-main content-wrap-gray">
        <PurchaseAlbumHeaderContainer album={album} />
        {shouldDisplayBrandAttribution(album) && (
          <PhotographerAttributionBanner account={album.creator} />
        )}
        <ContentWrap>
          <div className="ppd-checkout-modal">
            <h3 className="text-dark text-bold">Review your Order</h3>
            <div>{cart.map(cartItem => this.renderCartItem(cartItem))}</div>

            {shippingSelectableAlbumCustomPrints.length > 0 && (
              <AlbumCustomPrintShippingSelection
                albumCustomPrints={shippingSelectableAlbumCustomPrints}
                onShippingSelection={this.onAlbumCustomPrintShippingSelect}
                className="mt-2"
              />
            )}

            {hasShippableItems && (
              <Fragment>
                {!get(userAddress, 'id') && (
                  <Alert type="info" className="mt-2 address-notice">
                    We require your address to ship your prints. Please provide
                    it to complete your purchase.
                  </Alert>
                )}

                {showAddressMissingAlert && !canPurchase && (
                  <Alert type="warning" className="mt-2">
                    Please add a shipping address.
                  </Alert>
                )}

                <PurchasePpdAddressFormContainer
                  address={userAddress}
                  userId={userId}
                  addressDisplayTitle="Shipped to:"
                />
              </Fragment>
            )}

            <CheckoutPrice
              loading={loadingPrice}
              finalPrice={totalPrice}
              shippingPrice={shippingPrice}
              taxPrice={taxes}
              className="mt-2"
            />

            <div className="modal-footer">
              {error && (
                <Alert type="danger">
                  Uh oh! We came across an error while trying to process your
                  cart, please try again in a while.
                </Alert>
              )}
              {canPurchase ? (
                <div className="sticky-bottom">
                  <StripeCheckout
                    name="Waldo Photos"
                    description="Subscriptions Checkout"
                    image={getAsset('/waldo-stripe-logo.png')}
                    token={this.onReceivedStripeToken}
                    panelLabel={`Pay $${
                      (formatPriceForDisplay(totalPrice), true)
                    }`}
                    stripeKey={config.stripe.key}
                    currency="USD"
                    ComponentClass="div"
                    zipCode
                  >
                    <Button
                      btnBlock
                      className="text-center mb-1"
                      disabled={loading || loadingPrice || !canPurchase}
                      id="submit-button"
                    >
                      {loading ? 'Loading...' : 'Confirm & Check Out'}
                    </Button>
                  </StripeCheckout>
                </div>
              ) : (
                <Button
                  btnBlock
                  id="submit-button"
                  className="text-center disabled"
                  onClick={() => {
                    if (!(hasShippableItems && !!get(userAddress, 'id'))) {
                      this.setState(() => ({
                        showAddressMissingAlert: true,
                      }));
                    }
                  }}
                >
                  {loading ? 'Loading...' : 'Confirm & Checkout'}
                </Button>
              )}
              <Button
                onClick={() => {
                  history.push(`/purchase/album/${album.id}`);
                }}
                btnBlock
                btnStyle="secondary"
                className="text-center"
              >
                Continue Shopping
              </Button>
            </div>
          </div>
        </ContentWrap>
      </div>
    );
  }
}

export default PurchasePpdCheckout;
